"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AEList = void 0;
const echain_shared_quantities_1 = require("@igus/echain-shared-quantities");
const ArticleList_1 = require("../ArticleList");
const ArticleUtils_1 = require("../ArticleUtils");
const AE_1 = require("./AE");
const echain_shared_articles_1 = require("@igus/echain-shared-articles");
/**
 * The default order sorted `Ausfuehrung` items.
 */
const defaultAEAusfuehrungenOrder = {
    kunststoff_pendelnd: 1,
    kunststoff_starr: 2,
    kunststoff_enge_einbauverhaeltnisse_pendelnd: 3,
    KMA_pendelnd: 4,
    KMA_lang_pendelnd: 5,
    KMA_kurz_pendelnd: 6,
    KMA_starr: 7,
    KMA_lang_starr: 8,
    KMA_kurz_starr: 9,
    stahl_starr: 10,
};
/**
 * The `AEList` manages a list of `AE`
 */
class AEList extends ArticleList_1.ArticleList {
    constructor() {
        super();
    }
    static fromScratch() {
        return new AEList();
    }
    static fromJson(json, cloneStrategy = 'deep') {
        const list = new AEList();
        list.fromJson(json, cloneStrategy);
        return list;
    }
    static fromModel(model) {
        const list = new AEList();
        list.fromModel(model);
        return list;
    }
    static fromArray(arr) {
        const list = new AEList();
        list.fromArray(arr);
        return list;
    }
    static fromExistingArtNr(json, cloneStrategy = 'deep') {
        const list = new AEList();
        list.fromExistingArtNr(json, cloneStrategy);
        return list;
    }
    static fromInventedArtNr(json, cloneStrategy = 'deep') {
        const list = new AEList();
        list.fromInventedArtNr(json, cloneStrategy);
        return list;
    }
    fromJson(json, cloneStrategy = 'deep') {
        this.items = json.map((j) => AE_1.AE.fromJson(j, cloneStrategy));
        this.model = this.items.map((i) => i.toModel());
        this.emitChange();
    }
    fromModel(model) {
        this.items = model.map((m) => AE_1.AE.fromModel(m));
        this.model = model;
        this.emitChange();
    }
    fromExistingArtNr(json, cloneStrategy = 'deep') {
        this.items = [];
        for (const itemJson of json) {
            this.items.push(AE_1.AE.fromExistingArtNr(itemJson, cloneStrategy));
        }
        this.emitChange();
    }
    fromInventedArtNr(json, cloneStrategy = 'deep') {
        this.items = [];
        for (const itemJson of json) {
            this.items.push(AE_1.AE.fromInventedArtNr(itemJson, cloneStrategy));
        }
        this.emitChange();
    }
    clone() {
        return AEList.fromArray(this.items.map((i) => i.clone()));
    }
    toJson() {
        return this.items.map((i) => i.toJson());
    }
    toModel() {
        return this.model;
    }
    group(options) {
        return ArticleUtils_1.ArticleUtils.groupArticles(this.items, options);
    }
    /**
     * Retrieves the cumulated weight of all AEs in the list.
     *
     * @returns The weight.
     */
    getCumulatedWeight() {
        return echain_shared_quantities_1.Weight.kg(this.items.reduce((cumulatedWeight, ae) => { var _a, _b; return cumulatedWeight + ((_b = (_a = ae.gewicht) === null || _a === void 0 ? void 0 : _a.kg) !== null && _b !== void 0 ? _b : 0); }, 0));
    }
    /**
     * Retrieves the cumulated length of all AEs in the list.
     *
     * @returns The length.
     */
    getCumulatedLength() {
        return echain_shared_quantities_1.Length.cm(this.items.reduce((cumulatedLength, ae) => { var _a, _b; return cumulatedLength + ((_b = (_a = ae.laenge) === null || _a === void 0 ? void 0 : _a.cm) !== null && _b !== void 0 ? _b : 0); }, 0));
    }
    /**
     * Retrieves a new list with the filtered AEs.
     */
    filterByKetteRef(ketteId) {
        return AEList.fromArray(this.items.filter((ae) => { var _a; return Boolean((_a = ae.ketteRef) === null || _a === void 0 ? void 0 : _a.includes(ketteId)); }));
    }
    /**
     * Returns a new list with the filtered AEs.
     */
    filterByAusfuehrung(ausfuehrung) {
        return AEList.fromArray(this.items.filter((ae) => ae.getAusfuehrung() === ausfuehrung));
    }
    /**
     * Returns a new list with the filtered AEs.
     */
    filterByVariante(variante) {
        return AEList.fromArray(this.items.filter((ae) => ae.variante === variante));
    }
    /**
     * Returns a new list with the filtered AEs.
     */
    filterByBefestigungsVariante(befestigungsVariante) {
        return AEList.fromArray(this.items.filter((ae) => ae.befestigungsvariante === befestigungsVariante));
    }
    /**
     * Returns a new list with the filtered AEs.
     */
    filterByEinbauart(einbauart) {
        return AEList.fromArray(this.items.filter((ae) => { var _a; return Boolean((_a = ae.einbauart) === null || _a === void 0 ? void 0 : _a.includes(einbauart)); }));
    }
    /**
     * Returns a new list with AEList filtered by set ArtNrCountry.
     */
    filterByArtNrCountry(artNrCountry) {
        return AEList.fromArray(this.items.filter((article) => { var _a; return Boolean((_a = article.localizedArtNr) === null || _a === void 0 ? void 0 : _a[artNrCountry]); }));
    }
    /**
     * Returns a Set of all available Ausfuehrungen in this list
     */
    getAusfuehrungen() {
        const ausfuehrungen = new Set();
        this.items.forEach((ae) => {
            const ausfuehrung = ae.getAusfuehrung();
            if (ausfuehrung != null) {
                ausfuehrungen.add(ausfuehrung);
            }
        });
        return ausfuehrungen;
    }
    /**
     * Returns a sorted Set of all available Ausfuehrungen based on given order.
     * If the order of an `Ausfuehrung` is not defined it will be attached at the end of the list.
     */
    getSortedAusfuehrungen(order = defaultAEAusfuehrungenOrder) {
        const ausfuehrungenWithOrder = [];
        const ausfuehrungenWithoutOrder = [];
        Array.from(this.getAusfuehrungen()).forEach((ausfuehrung) => {
            if (order[ausfuehrung] == null) {
                ausfuehrungenWithoutOrder.push(ausfuehrung);
            }
            else {
                ausfuehrungenWithOrder.push(ausfuehrung);
            }
        });
        const ausfuehrungenSorted = ausfuehrungenWithOrder.sort((a, b) => order[a] - order[b]);
        return new Set(ausfuehrungenSorted.concat(ausfuehrungenWithoutOrder));
    }
    /**
     * Returns a Set of all available Varianten in this list
     */
    getVarianten() {
        const varianten = new Set();
        this.items.forEach((ae) => {
            if (ae.variante != null) {
                varianten.add(ae.variante);
            }
        });
        return varianten;
    }
    /**
     * Returns true if AELis have mounted and unmounted AEs
     */
    hasMontierteAndUnmontierteAes() {
        const montierteAEs = [];
        const unmontierteAEs = [];
        this.items.forEach((ae) => {
            if (ae.montiert)
                montierteAEs.push(ae);
            else if (!ae.montiert) {
                unmontierteAEs.push(ae);
            }
        });
        return montierteAEs.length !== 0 && unmontierteAEs.length !== 0;
    }
    /**
     * Returns a Set of all available BefestigungsVarianten in this list
     */
    getBefestigungsVarianten() {
        const befestigungsVarianten = new Set();
        this.items.forEach((ae) => {
            if (ae.befestigungsvariante != null) {
                befestigungsVarianten.add(ae.befestigungsvariante);
            }
        });
        return befestigungsVarianten;
    }
    /**
     * Tries to locate and return an AE of this current list by following criteria:
     *
     * The ae is searched in each Ausfuehrung in order of 'defaultAEAusfuehrungenOrder'
     *    filter list for "Ausfuehrung"
     *      check if filtered list has a "C-Profil", if ae exist return it.
     *      check if filtered list has a "Kamm", if ae exist return it.
     *      check if filtered list has A2 "Befestigungsvariante", if ae exist return it.
     *
     * If no criteria matches, return the first element of the list.
     * If the list empty, return undefined.
     */
    getDefault(befestigungsvariante = echain_shared_articles_1.AEBefestigungsvariante.A2) {
        for (const ausfuehrung of Array.from(this.getSortedAusfuehrungen())) {
            const aesFilteredByAusfuehrung = this.filterByAusfuehrung(ausfuehrung).toArray();
            const cProfilAEs = aesFilteredByAusfuehrung.filter((ae) => ae.hasCProfil());
            if (cProfilAEs.length) {
                return cProfilAEs.find((ae) => ae.befestigungsvariante === befestigungsvariante) || cProfilAEs[0];
            }
            const kammAEs = aesFilteredByAusfuehrung.filter((ae) => ae.hasKamm());
            if (kammAEs.length) {
                return kammAEs.find((ae) => ae.befestigungsvariante === befestigungsvariante) || kammAEs[0];
            }
            const aeFilteredByBesfestigungsvariante = aesFilteredByAusfuehrung.find((ae) => ae.befestigungsvariante === befestigungsvariante);
            if (aeFilteredByBesfestigungsvariante) {
                return aeFilteredByBesfestigungsvariante;
            }
        }
        return this.items.length ? this.items[0] : undefined;
    }
    getExampleAEPerAusfuehrung() {
        const exampleAEperAusfuehrung = AEList.fromScratch();
        this.getSortedAusfuehrungen().forEach((ausfuehrung) => {
            const exampleAE = this.items.find((ae) => ae.getAusfuehrung() === ausfuehrung);
            if (exampleAE) {
                exampleAEperAusfuehrung.add(exampleAE);
            }
        });
        return exampleAEperAusfuehrung;
    }
    /**
     * Returns an AEList with only AEs which have the properties ausrichtung,
     * befestigungsvariante, einbauart, typ and variante set.
     *
     * This properties will be need to show an AE on the AE-selection in echain-expert.
     */
    getAEsWithSelectionProperties() {
        return AEList.fromArray(this.items.filter((ae) => ae.befestigungsvariante && ae.ausrichtung && ae.variante && ae.typ && ae.einbauart));
    }
    isAEUnrecommended(selectedEinbauart, ae) {
        const mapping = {
            [echain_shared_articles_1.Einbauart.freitragend]: echain_shared_articles_1.AEEinbauart.freitragend,
            [echain_shared_articles_1.Einbauart.haengend]: echain_shared_articles_1.AEEinbauart.haengend,
            [echain_shared_articles_1.Einbauart.gleitend]: echain_shared_articles_1.AEEinbauart.gleitend,
        };
        if (selectedEinbauart) {
            const filteredAEListByEinbauart = this.filterByEinbauart(mapping[selectedEinbauart]);
            for (const filteredAe of filteredAEListByEinbauart.toArray()) {
                if (ae.id === filteredAe.id)
                    return false;
            }
        }
        return true;
    }
}
exports.AEList = AEList;
//# sourceMappingURL=AEList.js.map